/**
 * MobileTodaysCutCard Component
 *
 * Card-based display for today's cut data on mobile devices.
 */

import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { colors, typography as typographyTokens, radii, shadows } from '@components/design-system';

interface TodayCutData {
    ParentCompanyName: string;
    ResellerCompanyName: string;
    LocationName: string;
    totalCuts: number;
    successfulCuts: number;
    warrantyClaimCuts: number;
}

interface MobileTodaysCutCardProps {
    data: TodayCutData;
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode; highlight?: boolean }> = ({ label, value, highlight }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.xs,
                color: colors.neutral[500],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.sm,
                color: highlight ? colors.warning.dark : colors.neutral[700],
                fontWeight: highlight ? typographyTokens.fontWeight.semibold : typographyTokens.fontWeight.medium,
                textAlign: 'right',
                maxWidth: '60%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
            {value || '-'}
        </Typography>
    </Box>
);

export const MobileTodaysCutCard: React.FC<MobileTodaysCutCardProps> = ({ data }) => {
    const warrantyPercentage = data.totalCuts > 0 ? Math.round((data.warrantyClaimCuts / data.totalCuts) * 100) : 0;

    return (
        <Box
            sx={{
                display: 'block',
                backgroundColor: colors.neutral[0],
                borderRadius: `${radii.lg}px`,
                border: `1px solid ${colors.neutral[200]}`,
                p: 2,
                mb: 1.5,
                boxShadow: shadows.card,
            }}>
            {/* Header with icon and location info */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: `${radii.md}px`,
                        backgroundColor: `${colors.primary[500]}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <StorefrontOutlinedIcon sx={{ color: colors.primary[500], fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.md,
                            fontWeight: typographyTokens.fontWeight.semibold,
                            color: colors.neutral[900],
                            lineHeight: 1.3,
                        }}>
                        {data.LocationName}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xl,
                            fontWeight: typographyTokens.fontWeight.bold,
                            color: colors.primary[600],
                        }}>
                        {data.totalCuts}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[500],
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}>
                        Cuts
                    </Typography>
                </Box>
            </Box>

            {/* Details */}
            <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
                <InfoRow label='Reseller' value={data.ResellerCompanyName} />
                <InfoRow label='Company' value={data.ParentCompanyName} />
                <InfoRow label='Warranty' value={`${warrantyPercentage}%`} highlight />
            </Box>
        </Box>
    );
};

// Loading skeleton for mobile cards
export const MobileTodaysCutCardSkeleton: React.FC = () => (
    <Box
        sx={{
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Skeleton variant='rounded' width={40} height={40} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='70%' height={24} />
            </Box>
            <Box sx={{ textAlign: 'right' }}>
                <Skeleton variant='text' width={40} height={28} />
                <Skeleton variant='text' width={40} height={14} />
            </Box>
        </Box>
        <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
        </Box>
    </Box>
);

export default MobileTodaysCutCard;
